import React, { useCallback, useState, useContext } from "react"
import { LocaleContext } from "@src/context/locale-context"
import { validateEmail, required } from "@src/utils/validators"
import { FormActions } from "../../reusable/forms/auth-forms.styled"
import InputCountry from "../../reusable/forms/input-country"
import InputPhoneNumber from "../../reusable/forms/input-phone-number"
import InputCheckbox from "../../reusable/forms/input-checkbox"
import { formatPhoneNumber } from "@src/utils/formatters"
import InputDatePicker from "../../reusable/forms/input-datepicker"
import InputNumberButtons from "../../reusable/forms/input-number-buttons"
import Input from "../../reusable/forms/input"
import InputArea from "../../reusable/forms/input-area"
import styled from "styled-components"
import { useFormContext } from "react-hook-form"
import Button from "../../core-button"
import ModalContainer from "@src/components/container-modal"
import {
  formatBookingRequestData,
  generateBookingRequestDataLayer,
} from "./form-booking-functions"
import AffiliateReferrals, {
  createAffiliateOrder,
} from "@src/services/affiliates/referrals"
import GTM from "@src/services/gtm"
import { navigate } from "@src/components/core-link"
import NetlifyForms from "@src/services/netlify/forms"

const Row = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 500px) {
    flex-direction: column;
  }
`

const CaveatText = styled.p`
  font-family: "system-ui";
  font-style: italic;
  font-weight: 600;
  font-size: 12px;
  line-height: 2vh;
  color: #108b59;
  margin-top: 24px;
  margin-bottom: 24px;
  ${props => props.error && `color: #8b2010;`}
`

const BookingForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  max-width: 658px;

  p {
    margin-bottom: 0;
  }

  h2 {
    margin-bottom: 0;
  }
`

const Link = styled.div`
  text-decoration: underline;
`

const Paragraph = styled.div`
  margin-bottom: 10px;
`

const StyledButton = styled(Button)`
  max-width: 240px;
  padding: 16px 36px 17px;
  @media only screen and (${props => props.theme.screen.small.max}) {
    width: 100%;
  }
`

function FormBooking({ availability, bookingTerms, children, product }) {
  const [formStatus, setFormStatus] = useState({
    success: `This is the start of the booking process. We'll review availability
  for your tour request & contact you within 24 hours.`,
  })

  const { currency: localeCurrency, convertCurrency } =
    useContext(LocaleContext)

  const { setValue, trigger, handleSubmit, watch } = useFormContext()

  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false)

  const toggleTermsAndConditions = useCallback(
    open => {
      if (open) {
        setIsTermsModalOpen(true)
      } else {
        setIsTermsModalOpen(false)
      }
    },
    [setIsTermsModalOpen],
  )
  const handleTermsAndConditionsAgreement = useCallback(
    accepted => {
      setValue("agree_terms_10adventures", accepted)
      trigger("agree_terms_10adventures")
      toggleTermsAndConditions(false)
    },
    [setValue, trigger, toggleTermsAndConditions],
  )

  const onSubmit = async submitValues => {
    setFormStatus({
      success: "Please wait a moment while we submit your request...",
    })
    const { subscribe_newsletter } = submitValues
    const dataLayer = generateBookingRequestDataLayer(
      submitValues,
      product,
      localeCurrency,
      convertCurrency,
    )
    GTM.dataLayerPush({
      event: "10a.booking.success",
      ...dataLayer,
    })
    if (subscribe_newsletter) {
      GTM.dataLayerPush({
        event: "10a.subscription.success",
        subscribed: true,
      })
    }
    // refCode section creates order in GoAffPro
    const refCode = AffiliateReferrals.getActiveReferral()
    if (refCode) {
      createAffiliateOrder({
        order: {
          number: `Inquiry - ${product.title} - ${Date.now()}`,
          total: 0,
        },
        ref_code: refCode,
      })
    }
    setTimeout(function () {
      setFormStatus({
        success:
          "Thanks for the message! Our team will get back to you soon. Please contact us if you do not receive a booking confirmation.",
      })
      navigate("/bookings/thank-you/")
    }, "500")
  }

  const submit = async data => {
    data.details = {
      "room-type": data["room-type"],
      extras: data["extras"],
    }
    data["room-type"] = undefined
    data["extras"] = undefined

    const submitData = formatBookingRequestData(data, product, localeCurrency)

    try {
      const result = {}
      result.submit = await onSubmit(submitData)

      result.netlify = await NetlifyForms.submitForm(
        "booking-request-zoho",
        submitData,
      )
    } catch (error) {
      console.error("Submit failed", error)
      setFormStatus({
        error: "Form submission failed, try again later",
      })
    }
  }

  const email = watch("email")

  const validateConfirmEmail = value => {
    if (value !== email) {
      return "Emails must match"
    }
    return true
  }

  const agree = value => {
    if (value) return true
    return "You must agree to the terms and conditions to book this trip."
  }

  const filterTravelDate = useCallback(
    checkDate => {
      if (availability) {
        if (
          availability.fixedDates &&
          availability.fixedDates.length &&
          !availability.fixedDates.some(check => {
            let departureDateParse

            // get departure date
            departureDateParse = check.start.split("-")
            if (departureDateParse.length !== 3) {
              return false
            }
            const departureDate = new Date()
            departureDate.setFullYear(
              departureDateParse[0],
              parseInt(departureDateParse[1]) - 1,
              departureDateParse[2],
            )
            departureDate.setHours(0, 0, 0, 0)

            return departureDate.getTime() === checkDate.getTime()
          })
        ) {
          return false
        } else if (
          availability.daysOfWeek &&
          !availability.daysOfWeek.includes(checkDate.getDay())
        ) {
          return false
        } else if (
          availability.months &&
          !availability.months.includes(checkDate.getMonth() + 1)
        ) {
          return false
        }
      }
      const currentDate = new Date()
      return checkDate > currentDate
    },
    [availability],
  )

  return (
    <>
      <BookingForm>
        <ModalContainer
          id="tour-booking-terms-modal"
          isOpen={isTermsModalOpen}
          onClose={() => toggleTermsAndConditions(false)}
          title={bookingTerms && bookingTerms.title}
          content={bookingTerms && bookingTerms.content}
          variant="full-height"
          actions={
            <>
              <p>Do you agree to the 10Adventures Terms & Conditions?</p>
              <Button
                icon="close"
                size="small"
                color="outline"
                onClick={() => handleTermsAndConditionsAgreement(false)}
              >
                I do not agree
              </Button>
              <Button
                icon="check"
                size="small"
                onClick={() => handleTermsAndConditionsAgreement(true)}
              >
                I agree
              </Button>
            </>
          }
        />
        <h2>Contact Information</h2>
        <p>
          {`Please share contact details for the person coordinating this booking.
        This person doesn't have to be the person who will pay for the tour.`}
        </p>
        <Row>
          <Input
            name="name_first"
            label="First name"
            required={true}
            placeholder="Enter first name"
            validate={required()}
          />
          <Input
            name="name_last"
            label="Last name"
            required={true}
            placeholder="Enter last name"
            validate={required()}
          />
        </Row>
        <Row>
          <InputCountry
            name="nationality"
            label="Nationality"
            placeholder="Select nationality"
          />
          <InputPhoneNumber
            name="phone_number"
            label="Phone Number"
            placeholder="090..."
            formatter={formatPhoneNumber}
          />
        </Row>
        <Input
          name="email"
          label="Email"
          required={true}
          placeholder="Email address"
          validate={validateEmail()}
        />
        <Input
          name="email-confirm"
          label="Email confirmation"
          required={true}
          placeholder="Confirm email address"
          validate={validateConfirmEmail}
        />

        <InputCheckbox name="subscribe_newsletter">
          Subscribe to our newsletter for free gear giveaways and discounts on
          adventure travel!
        </InputCheckbox>
      </BookingForm>
      <BookingForm>
        <h2>Your trip</h2>
        <p>
          {`Please select your preferred tour start date for accurate pricing and availability. If your dates are unavailable, please select the closest possible start date and leave a note in the 'Special Requests' section below.`}
        </p>
        <InputDatePicker
          name="travel_date"
          label="Preferred travel start date"
          icon="calendar-dates"
          placeholder={"Pick a date"}
          displayFormat="MMMM d, yyyy"
          filterDate={filterTravelDate}
          validate={required()}
          required={true}
        />
        <InputNumberButtons
          name="traveller_count"
          label="Number of travellers"
          min={1}
        />
        <InputArea
          name="room-type"
          label="Room type"
          placeholder="Enter number of single/double/triple/other"
        />
        <InputArea
          name="extras"
          label="Special requests"
          placeholder="I.e. food allergies, additional activities, itinerary changes"
        />
      </BookingForm>
      <BookingForm>
        <h2>Have a coupon code?</h2>
        <Input
          name="coupon-code"
          label="Coupon code"
          placeholder="Enter coupon code"
        />
      </BookingForm>
      <BookingForm>
        <h2>Terms & Conditions</h2>
        <div>
          <Paragraph>
            To complete this booking request I acknowledge that I have read and
            accept the following:
          </Paragraph>
          <InputCheckbox name="agree_terms_10adventures" validate={agree}>
            <Link onClick={() => toggleTermsAndConditions(true)}>
              10Adventures Terms & Conditions
            </Link>
          </InputCheckbox>
        </div>
        {formStatus.success && <CaveatText>{formStatus.success}</CaveatText>}

        {formStatus.error && (
          <CaveatText error={true}>{formStatus.error}</CaveatText>
        )}
        <StyledButton
          type="submit"
          icon="calendar-dates"
          onClick={() => handleSubmit(submit)()}
        >
          Start your booking
        </StyledButton>
      </BookingForm>

      <FormActions>{children}</FormActions>
    </>
  )
}

export default FormBooking
